import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../auth'

/**
 * Per-user mute. A muted player's weekly submissions render as "muted"
 * for the muter only (a personal display preference — reader-initiated
 * hide for user-generated content). Backed by the `user_mutes` table,
 * RLS-scoped to the current user.
 */

// `user_mutes` isn't in the generated DB types, so use an untyped client
// for these (same pattern as other post-generation tables).
const db = supabase as any

export async function fetchMyMutedIds(): Promise<string[]> {
  const { data, error } = await db.from('user_mutes').select('muted_id')
  if (error) throw error
  return (data ?? []).map((r: { muted_id: string }) => r.muted_id)
}

export async function muteUser(targetId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not signed in')
  const { error } = await db
    .from('user_mutes')
    .insert({ muter_id: user.id, muted_id: targetId })
  // Ignore duplicate-key (already muted) so the toggle is idempotent.
  if (error && error.code !== '23505') throw error
}

export async function unmuteUser(targetId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not signed in')
  const { error } = await db
    .from('user_mutes')
    .delete()
    .eq('muter_id', user.id)
    .eq('muted_id', targetId)
  if (error) throw error
}

/**
 * Loads the current user's muted-id set once. Returns the set plus a
 * refresh callback. Guests (no session) get an empty set and never hit
 * the network.
 */
export function useMutedIds(): { mutedIds: Set<string>; refresh: () => void } {
  const { session } = useAuth()
  const [mutedIds, setMutedIds] = useState<Set<string>>(new Set())

  const refresh = useCallback(() => {
    if (!session) { setMutedIds(new Set()); return }
    fetchMyMutedIds()
      .then(ids => setMutedIds(new Set(ids)))
      .catch(() => { /* non-fatal: just show content unmuted */ })
  }, [session])

  useEffect(() => { refresh() }, [refresh])

  return { mutedIds, refresh }
}

/** Replacement text shown in place of a muted player's submission. */
export const MUTED_PLACEHOLDER = 'muted'
